import { ArrowDown, RouteIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import {
  type FindShortestPathArgs,
  type FindShortestPathResult,
  primaryLabel,
} from '@/features/agent/tools/find-shortest-path'
import { useToolCardOpen } from '@/features/agent/tools/useToolCardOpen'
import { ToolCallCard, ToolCallHeader, type ToolCallStatus } from './_shared/ToolCallHeader'

export interface FindShortestPathViewProps {
  args: FindShortestPathArgs
  parsed: FindShortestPathResult | null
  running: boolean
  errored: boolean
  /** Resolved action footer, supplied by the connected tool. Pure stories
   *  pass a mock `<ToolCardActions>` here to render the footer. */
  actions?: ReactNode
  /** Force the card expanded even with no body. Defaults to false; the card
   *  auto-expands when it has results and stays collapsed for empty or failed
   *  calls. Stories pass true to render the expanded body. */
  defaultOpen?: boolean
}

/** Pure presentation for a `find_shortest_path` tool-result card. */
export function FindShortestPathView({
  args: _args,
  parsed,
  running,
  errored,
  actions,
  defaultOpen = false,
}: FindShortestPathViewProps) {
  const steps = parsed?.steps ?? []
  const hasBody = !running && (errored || steps.length > 0)
  const { open, onToggle } = useToolCardOpen(hasBody, defaultOpen, errored)

  if (!running && parsed?.status === 'empty') return null

  const hopCount = parsed?.path_length ?? Math.max(0, steps.length - 1)
  const subject =
    parsed?.source && parsed.target ? `${parsed.source.name} → ${parsed.target.name}` : undefined

  const headerStatus: ToolCallStatus = running ? 'running' : errored ? 'error' : 'complete'
  const description = running ? (
    'Finding path'
  ) : errored ? (
    'Failed'
  ) : subject ? (
    <>
      {hopCount === 1 ? '1 hop' : `${hopCount} hops`}
      <span className="mx-1.5">·</span>
      <span className="text-foreground">{subject}</span>
    </>
  ) : (
    'Path'
  )

  return (
    <ToolCallCard>
      <ToolCallHeader
        status={headerStatus}
        icon={RouteIcon}
        toolName="find_shortest_path"
        description={description}
        expandable={hasBody}
        open={open}
        onToggle={onToggle}
      />

      {open && (
        <div className="flex flex-col gap-2 border-t border-border px-3 py-2">
          {parsed?.status === 'error' && (
            <p className="whitespace-pre-wrap text-xs text-muted-foreground">
              {parsed.error ?? 'Unknown error'}
            </p>
          )}
          {parsed?.status === 'ok' && steps.length > 0 && <PathSteps steps={steps} />}
          {parsed?.status === 'ok' && steps.length > 1 && actions && (
            <div className="border-t border-border pt-2">{actions}</div>
          )}
        </div>
      )}
    </ToolCallCard>
  )
}

function PathSteps({ steps }: { steps: NonNullable<FindShortestPathResult['steps']> }) {
  return (
    <ol className="flex flex-col text-xs">
      {steps.map((step, i) => {
        const label = primaryLabel(step.node.labels)
        return (
          <li key={`${step.node.element_id}-${i}`} className="flex flex-col">
            <div className="flex items-center gap-2 py-1">
              <span className="truncate font-semibold text-foreground">{step.node.name}</span>
              {label && (
                <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                  {label}
                </span>
              )}
            </div>
            {step.node.subtitle && (
              <p className="-mt-0.5 truncate pb-1 text-[11px] text-muted-foreground">
                {step.node.subtitle}
              </p>
            )}
            {step.relationship && (
              <div className="ml-2 flex items-center gap-1 border-l border-border py-1 pl-3 text-[10px] text-muted-foreground">
                <ArrowDown className="size-3 shrink-0" />
                <span className="font-mono">{step.relationship.type}</span>
              </div>
            )}
          </li>
        )
      })}
    </ol>
  )
}
